import cx from 'classnames';
import type { ReactElement, ReactNode } from 'react';
import { useState } from 'react';
import { createPortal } from 'react-dom';
import { Overlay } from '../Overlay';
import { Typography } from '../Typography';
import { ModalBody } from './Body';
import { ModalFooter } from './Footer';
import { ModalHeader } from './Header';
import { OnCloseContext } from './OnCloseContext';
import { Transition } from './Transition';
import { FadeIn } from './Transitions/FadeIn';
import { useCSSBodyClass } from './hooks/useCSSBodyClass';
import { useEscapeKey } from './hooks/useEscapeKey';
import { useIOSBodyFreeze } from './hooks/useIOSBodyFreeze';
import styles from './styles.module.css';

declare global {
  interface Document {
    scrollTop: number;
    clientTop: number;
  }
}

export interface ModalProps {
  visible: boolean;
  children: ReactNode;
  fullscreen?: boolean;
  fullHeight?: boolean;
  iosBodyFreeze?: boolean;
  appearAnimation?: boolean;
  onClose: () => void;
  onEntered?: () => void;
  onExited?: () => void;
}

export function Modal({
  visible,
  children,
  fullscreen,
  fullHeight,
  iosBodyFreeze,
  appearAnimation,
  onClose,
  onEntered,
  onExited,
}: ModalProps): ReactNode {
  // Close modal on Escape
  useEscapeKey(onClose, visible);
  // Add or remove class based on visible value
  useCSSBodyClass(visible);

  const [animationProgress, setAnimationProgress] = useState<boolean>(() => {
    return visible;
  });
  const handleEnter = (): void => {
    setAnimationProgress(true);
  };

  const handleOnExited = (): void => {
    setAnimationProgress(false);
    if (onExited) {
      onExited();
    }
  };

  // callbacks with iosFreeze
  const { handleOnEntered, handleOnExit } = useIOSBodyFreeze(iosBodyFreeze, onEntered);

  return (
    <OnCloseContext.Provider value={onClose}>
      {createPortal(
        <Typography.div
          base="body"
          className={cx({
            [styles.Hidden]: !animationProgress,
          })}
        >
          <div
            className={cx(styles.ModalContainer, {
              [styles.Fullscreen]: fullscreen,
              [styles.FullHeight]: fullHeight,
            })}
          >
            <Transition
              appear={appearAnimation}
              visible={visible}
              onEnter={handleEnter}
              onExit={handleOnExit}
              onEntered={handleOnEntered}
              onExited={handleOnExited}
            >
              <div className={styles.ContentLayout}>{children}</div>
            </Transition>
            <FadeIn appear={appearAnimation} visible={visible}>
              {(): ReactElement => <Overlay onClick={onClose} />}
            </FadeIn>
          </div>
        </Typography.div>,
        document.body,
      )}
    </OnCloseContext.Provider>
  );
}

Modal.Header = ModalHeader;
Modal.Body = ModalBody;
Modal.Footer = ModalFooter;
